import type { ReceiptItem } from "@skladiscenje/receipt";

import type { WireItem } from "../api/types.js";

/**
 * Printing.
 *
 * The receipt itself lives in `@skladiscenje/receipt`, rebuilt from the legacy
 * `printItem` (AddItemWindow.xaml.cs:552) and verified against reference output
 * captured from the WPF app. This module only decides *how* the rendered page
 * reaches a printer, which is the one thing that genuinely differs between the
 * two deployments:
 *
 *   - The desktop shell can drive a named printer with no dialog, which is what
 *     the counter has today — the operator saves and the slip appears.
 *   - A browser cannot. The best it can do is open the print dialog, so that is
 *     what the hosted build does.
 *
 * `GET /api/capabilities` reports which applies (`apps/server/src/capabilities.ts`)
 * and {@link selectPrintService} picks the implementation, so nothing above this
 * module branches on deployment.
 */

export interface PrintCapabilities {
  silent: boolean;
  enumeratePrinters: boolean;
}

export interface PrintRequest {
  item: WireItem;
  /** Whether the operator had "Tiskaj potrdilo" ticked for this save. */
  requested: boolean;
}

export type PrintResult =
  | { ok: true }
  | { ok: false; reason: "not-implemented" | "print-failed"; message?: string };

export interface PrintService {
  print(request: PrintRequest, capabilities: PrintCapabilities): Promise<PrintResult>;
}

/**
 * The slice of the Electron preload bridge this module needs.
 *
 * Declared structurally rather than imported from `apps/desktop`: the web app
 * has to build and run without the desktop package present, since it is also
 * the hosted deployment. The shape must stay in step with `preload.cts`, and
 * the tests cover that it is called the way that bridge expects.
 */
interface DesktopPrintBridge {
  settings: { get(): Promise<{ printerName: string | null }> };
  print: { silent(html: string, printerName: string | null): Promise<{ ok: boolean; message?: string }> };
}

function desktopBridge(): DesktopPrintBridge | null {
  const bridge = (globalThis as { desktop?: DesktopPrintBridge }).desktop;
  return bridge && typeof bridge.print?.silent === "function" ? bridge : null;
}

/**
 * Wire shape to receipt shape: the API serialises dates as ISO strings because
 * JSON has no date type, and the renderer wants real `Date`s.
 */
export function toReceiptItem(item: WireItem): ReceiptItem {
  return {
    number: item.number,
    name: item.name,
    lastname: item.lastname,
    phone: item.phone,
    carMaker: item.carMaker,
    registrationNumber: item.registrationNumber,
    tireSets: item.tireSets,
    notes: item.notes,
    washing: item.washing,
    receivedAt: item.receivedAt === null ? null : new Date(item.receivedAt),
    receivedBy: item.receivedBy,
  };
}

/**
 * Renders the receipt HTML, loading `@skladiscenje/receipt` on demand. That
 * package embeds the Arimo font as base64 (~280 KB in `assets.generated.ts`),
 * which has no business sitting in the initial bundle when printing is a rare,
 * end-of-transaction action — a dynamic `import()` puts it in its own chunk that
 * only downloads the first time someone actually prints.
 */
async function renderReceiptHtml(item: WireItem): Promise<string> {
  const receipt = await import("@skladiscenje/receipt");
  return receipt.renderReceiptHtml(toReceiptItem(item));
}

/** Prints with no dialog through the desktop shell — parity with the WPF app. */
export function createDesktopPrintService(
  getBridge: () => DesktopPrintBridge | null = desktopBridge,
): PrintService {
  return {
    async print(request) {
      const bridge = getBridge();
      if (!bridge) {
        return { ok: false, reason: "not-implemented", message: "Tiskanje ni na voljo v tem oknu." };
      }
      try {
        const settings = await bridge.settings.get();
        const html = await renderReceiptHtml(request.item);
        const result = await bridge.print.silent(html, settings.printerName);
        if (result.ok) return { ok: true };
        // Spread rather than assign: the bridge may omit `message`, and
        // `exactOptionalPropertyTypes` distinguishes "absent" from "undefined".
        return { ok: false, reason: "print-failed", ...(result.message === undefined ? {} : { message: result.message }) };
      } catch (error) {
        return {
          ok: false,
          reason: "print-failed",
          message: error instanceof Error ? error.message : String(error),
        };
      }
    },
  };
}

/**
 * Prints through the browser's own dialog.
 *
 * The receipt goes into an off-screen iframe rather than replacing the page, so
 * the operator keeps their place in the form; navigating away mid-save would be
 * worse at a counter than an extra dialog.
 *
 * Two things this must not do, both learned the hard way:
 *
 *   - **Never leave the promise unsettled.** The save flow awaits this before
 *     confirming, so a load event that never arrives would hang the
 *     confirmation for a save that already succeeded — the operator would
 *     assume it failed and enter the tires twice. Every path settles, and a
 *     deadline covers the ones that do not arrive on their own.
 *   - **Never make the operator wait for the dialog.** The print dialog is
 *     modal and may sit open for as long as they like. This resolves as soon as
 *     the dialog has been asked for, and cleans the iframe up afterwards.
 */
export function createBrowserPrintService(doc: Document = document, loadDeadlineMs = 2000): PrintService {
  return {
    async print(request) {
      // Load the receipt (and its embedded font) before touching the DOM. A
      // failure here is the same "could not prepare" outcome as a missing iframe
      // window, so the operator is told rather than left waiting.
      let html: string;
      try {
        html = await renderReceiptHtml(request.item);
      } catch (error) {
        return { ok: false, reason: "print-failed", message: error instanceof Error ? error.message : String(error) };
      }
      return new Promise<PrintResult>((resolve) => {
        let settled = false;
        const finish = (result: PrintResult) => {
          if (settled) return;
          settled = true;
          resolve(result);
        };

        let frame: HTMLIFrameElement | null = null;
        const cleanup = () => {
          frame?.remove();
          frame = null;
        };

        const openDialog = () => {
          const win = frame?.contentWindow;
          if (!win) {
            cleanup();
            finish({ ok: false, reason: "print-failed", message: "Tiskanja ni bilo mogoče pripraviti." });
            return;
          }
          try {
            win.addEventListener("afterprint", cleanup);
            win.focus();
            win.print();
            finish({ ok: true });
          } catch (error) {
            cleanup();
            finish({
              ok: false,
              reason: "print-failed",
              message: error instanceof Error ? error.message : String(error),
            });
          }
        };

        try {
          frame = doc.createElement("iframe");
          frame.setAttribute("aria-hidden", "true");
          frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
          frame.srcdoc = html;
          frame.onload = openDialog;
          doc.body.appendChild(frame);
          // `srcdoc` does not reliably fire `load` everywhere; try anyway
          // rather than stranding the caller.
          setTimeout(() => {
            if (!settled) openDialog();
          }, loadDeadlineMs);
        } catch (error) {
          cleanup();
          finish({
            ok: false,
            reason: "print-failed",
            message: error instanceof Error ? error.message : String(error),
          });
        }
      });
    },
  };
}

/**
 * Picks the implementation for this deployment.
 *
 * `silent` comes from the server's capabilities, but the preload bridge is
 * checked as well: a desktop-flagged server opened from an ordinary browser
 * over the shop LAN can only use the dialog, and claiming otherwise would leave
 * the operator waiting for a slip that never arrives.
 */
export function selectPrintService(capabilities: PrintCapabilities): PrintService {
  return capabilities.silent && desktopBridge() !== null
    ? createDesktopPrintService()
    : createBrowserPrintService();
}
